import type { BlockContentDto } from "@/types";
import { type ClassValue, clsx } from "clsx";
import { DEFAULT_LANGUAGE, LANGUAGE_STORAGE_KEY, LANGUAGE_TO_LOCALE, MANUAL_FLAG_KEY, SUPPORTED_LANGUAGES } from "@/constants/languages";

/**
 * Merge class names using clsx
 */
export function cn(...inputs: ClassValue[]) {
  return clsx(inputs);
}

/**
 * Parse block content from JSON string to typed object
 */
export function parseBlockContent<T>(block: BlockContentDto): T {
  try {
    return JSON.parse(block.content) as T;
  } catch (error) {
    console.error("Failed to parse block content:", error);
    return {} as T;
  }
}

/**
 * Format date string with optional locale support
 */
export function formatDate(dateString: string | undefined, lang?: string): string {
  if (!dateString) return "";
  const locale = (lang && LANGUAGE_TO_LOCALE[lang]) || "en-US";
  const date = new Date(dateString);
  return date.toLocaleDateString(locale, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

/**
 * Truncate text to specified length
 */
export function truncate(text: string, length: number): string {
  if (text.length <= length) return text;
  return text.substring(0, length).trim() + "...";
}

/**
 * Scroll to top of page
 */
export function scrollToTop(smooth = true): void {
  window.scrollTo({
    top: 0,
    behavior: smooth ? "smooth" : "auto",
  });
}

/**
 * Check if element is in viewport
 */
export function isInViewport(element: HTMLElement): boolean {
  const rect = element.getBoundingClientRect();
  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <=
      (window.innerHeight || document.documentElement.clientHeight) &&
    rect.right <= (window.innerWidth || document.documentElement.clientWidth)
  );
}

/**
 * Format reading time in minutes
 */
export function formatReadingTime(minutes: number): string {
  if (minutes <= 1) return '1 min read';
  return `${minutes} min read`;
}

export const normalizeHtml = (html: string) => {
  if (!html) return "";
  return (
    html
      // Normalize non-breaking spaces first
      .replace(/&nbsp;/g, " ")
      // Convert empty paragraphs (including &nbsp;) to line breaks
      .replace(/<p>(\s|&nbsp;)*<\/p>/gi, "<br />")
      // Remove remaining <p> tags but keep text
      .replace(/<\/?p>/gi, "")
      // Decode common entities
      .replace(/&#39;/g, "'")
  );
};


export const getActiveLanguage = (): string => {
  // Only honour localStorage if the user explicitly chose a language
  const wasManuallySet = localStorage.getItem(MANUAL_FLAG_KEY) === 'true';
  
  if (wasManuallySet) {
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (stored && SUPPORTED_LANGUAGES.some((l) => l.code === stored)) {
      return stored;
    }
  }
  
  // Clear any stale auto-detected value
  localStorage.removeItem(LANGUAGE_STORAGE_KEY);
  return DEFAULT_LANGUAGE;
};